/* global window, React, Icon, Button, useState, useMemo */
// ============================================================
// AI Regnskapsfører — MVA-meldingen i modal
// ============================================================
// MVA-raden kunne godkjennes med et klikk. Signaturen betydde ingenting: den
// hakket av oppgaven uten at en eneste melding ble opprettet, sendt eller
// signert — og neste gang raden ble åpnet var Godkjenn-knappen borte, mens
// terminen fortsatt sto ulevert hos Skatteetaten.
//
// Det brukeren skal godkjenne er selve MVA-meldingen. Derfor åpner «Åpne»
// meldingen her, over AI-siden, med det samme tallgrunnlaget og den samme
// kodeoversikten som på Innleveringer — det er `VatTermDetail`, ikke en
// slankere kopi. Forskjellen er ett steg i stedet for to: «Godkjenn og send
// inn» oppretter meldingen, sender den inn og signerer den. Etterpå står
// betalingen igjen, med kontonummer og KID fra Skatteetaten når koblingen mot
// Altinn er på plass.

// Terminene raden faktisk peker på. Agenten flagger dem som linjer, og linje-id
// er terminnøkkelen — da åpner modalen den terminen brukeren så i raden, ikke
// den første uleverte i året.
//
// Nøkkelen alene holder ikke: `term-6` finnes i hvert eneste år, og februar
// viser 6. termin i FJOR (den forfaller 10. februar). Uten årstallet ville
// modalen åpnet årets termin 6 — feil tallgrunnlag, og «Godkjenn og send inn»
// ville levert feil melding. Linjen bærer derfor `data.vatYear`.
const apVatLineKey = (line) => {
  const id = String(line?.id || '');
  const year = Number(line?.data?.vatYear);
  return id && Number.isFinite(year) ? `${year}:${id}` : id;
};
const apVatTermKeys = (term) => [
  `${term?.year}:${term?.key || ''}`,
  `${term?.year}:${term?.label || ''}`,
  // Linjer uten årstall (eldre oppgaver) matcher fortsatt på nøkkelen alene.
  String(term?.key || ''),
  String(term?.label || ''),
].filter((value) => value && !value.startsWith('undefined:') && !value.endsWith(':'));
// Årene modalen må hente. Normalt ett — fristtabellen legger aldri to årganger
// i den samme måneden — men listen utledes i stedet for å antas.
const apVatSurfaceYears = (task, fallbackYear) => {
  const years = [...new Set((task?.lines || [])
    .map((line) => Number(line?.data?.vatYear))
    .filter((value) => Number.isFinite(value)))];
  return years.length ? years.sort() : [Number(fallbackYear)];
};
const apVatCandidateTerms = (terms, task) => {
  const flagged = new Set((task?.lines || []).map(apVatLineKey).filter(Boolean));
  const open = (terms || []).filter((term) => term.status === 'ikke-levert' || term.status === 'arkiv');
  const picked = open.filter((term) => apVatTermKeys(term).some((key) => flagged.has(key)));
  return picked.length ? picked : open;
};

function ApVatTermPicker({ terms, onPick }) {
  if (!terms.length) return <div className="ap-surface-empty">
    <Icon name="check" size={22} />
    <div>
      <b>{t('ap.vatSurface.emptyTitle', 'Ingen MVA-termin å levere')}</b>
      <div className="t-sm muted" style={{ fontWeight: 600, marginTop: 2 }}>{t('ap.vatSurface.emptyDesc', 'Alle terminer i året står som levert.')}</div>
    </div>
  </div>;
  return <div className="col gap-10">
    <div className="t-sm muted" style={{ fontWeight: 700 }}>{t('ap.vatSurface.pickPrompt', 'Velg hvilken MVA-termin du vil kontrollere og sende inn.')}</div>
    {terms.map((term) => <button key={`${term.year}:${term.key}`} type="button" className="ap-surface-pick" onClick={() => onPick(`${term.year}:${term.key}`)}>
      <span className="icon-chip"><Icon name="percent" size={18} /></span>
      <span className="grow" style={{ minWidth: 0, textAlign: 'left' }}>
        <span className="t-h3" style={{ fontSize: 14 }}>{term.n ? `${term.n}. termin ${term.year}` : (term.label || t('ap.vatSurface.termFallback', 'MVA-termin'))}</span>
        <span className="t-sm muted" style={{ fontWeight: 600, display: 'block' }}>
          {term.period || ''}{term.frist ? ` · frist ${term.frist}` : ''}
        </span>
      </span>
      <Icon name="chevRight" size={16} />
    </button>)}
  </div>;
}

function ApVatSurfaceModal({ year, task, onClose, onChanged }) {
  const backend = window.PlummeBackend?.useBackend ? window.PlummeBackend.useBackend() : null;
  const [pickedKey, setPickedKey] = useState('');
  const [dlg, setDlg] = useState(null);
  const [changed, setChanged] = useState(false);
  // Raden kan peke på fjorårets termin. Modellen hentes derfor for årene
  // linjene faktisk nevner, ikke for året siden står i.
  const surfaceYears = apVatSurfaceYears(task, year);
  const model = useMemo(
    () => {
      if (!window.readVatFilingModel) return { terms: [], paymentAccounts: [] };
      const models = surfaceYears.map((entry) => window.readVatFilingModel(entry) || {});
      return {
        terms: models.flatMap((entry) => entry.terms || []),
        // Betalingskontoene hører til foretaket, ikke til året.
        paymentAccounts: models[0]?.paymentAccounts || [],
      };
    },
    [backend?.appState, backend?.activeBusinessId, surfaceYears.join(',')],
  );
  const terms = model.terms || [];
  const candidates = apVatCandidateTerms(terms, task);
  // Nøkkelen må bære året: to årganger i den samme lista har begge `term-6`.
  const termKey = (entry) => `${entry?.year}:${entry?.key || ''}`;
  const selectedKey = pickedKey || (candidates.length === 1 ? termKey(candidates[0]) : '');
  const term = selectedKey ? (terms.find((entry) => termKey(entry) === selectedKey) || null) : null;
  // Innsending og oppgjør endrer tallene AI-siden viser. Køen lastes når
  // modalen lukkes, ikke ved hvert klikk inne i meldingen.
  const actions = useMemo(() => window.createVatTermActions({
    onChanged: () => setChanged(true),
    onCloseDialog: () => setDlg(null),
  }), []);
  const close = () => { if (changed && onChanged) void onChanged(); onClose(); };
  const routeAway = (route) => { close(); if (route && window.plummeNav) window.plummeNav(route); };
  // Grunnlaget bak en kode er en bilagsliste, og den hører til Alle bilag.
  // Modalen lukkes først — ellers navigerer siden under en åpen modal.
  const openBasis = (code, basisTerm) => {
    const codeValue = String(code?.code || '');
    if (!codeValue) return;
    window.__plummeVoucherFilter = {
      filter: { months: Array.isArray(basisTerm?.months) ? basisTerm.months : [0, 11] },
      vat: {
        code: codeValue,
        desc: code?.desc || '',
        term: basisTerm?.n ? `${basisTerm.n}. termin ${basisTerm.year}` : String(basisTerm?.year || year),
        from: 'aiautopilot',
      },
    };
    routeAway('allebilag');
  };
  const Detail = window.VatTermDetail;
  return <React.Fragment>
    <window.ApSurfaceModal title={t('ap.vatSurface.title', 'MVA-melding')} onClose={close}
      subtitle={term ? `${term.n}. termin ${term.year}` : `${candidates.length} terminer`}
      footer={<React.Fragment>
        <Button variant="ghost" onClick={close}>{t('common.close', 'Lukk')}</Button>
        {/* Arkiv, tidligere terminer og oversikten hører til Innleveringer.
            De skal ikke dupliseres inn i en godkjenning. */}
        <Button variant="secondary" icon="arrowRight" onClick={() => routeAway('innsendinger')} data-help={t('helpTopics.taxSubmission.body')} data-help-title={t('helpTopics.taxSubmission.title')}>{t('ap.vatSurface.openSubmissions', 'Åpne innleveringer')}</Button>
      </React.Fragment>}>
      {term && Detail
        ? <Detail key={`${term.year}:${term.key}`} term={term} paymentAccounts={model.paymentAccounts || []} decls={term.declarations}
            submitMode="direct"
            onBack={() => { if (candidates.length > 1) setPickedKey(''); else close(); }}
            onTolldekl={(action, id) => { if (action === 'open') setDlg('tolldekl'); else if (action === 'annul') void actions.annulCustoms(id); }}
            onBetaling={() => setDlg('betaling')}
            onSavePayment={(record) => actions.savePayment(term, record)}
            onDeletePayment={(record) => actions.deletePayment(term, record)}
            onSubmit={(submitTerm, nextSubmitted, filing) => actions.submitTerm(submitTerm, nextSubmitted, filing)}
            onOpenBasis={openBasis} />
        : <ApVatTermPicker terms={candidates} onPick={setPickedKey} />}
    </window.ApSurfaceModal>
    {dlg === 'tolldekl' && window.TolldeklarasjonModal
      && <window.TolldeklarasjonModal term={term} onClose={() => setDlg(null)} onSave={(entry) => actions.saveCustoms(entry)} />}
    {dlg === 'betaling' && term && window.MvaBetalingModal
      && <window.MvaBetalingModal term={term} paymentAccounts={model.paymentAccounts || []}
          onClose={() => setDlg(null)} onSave={(record) => actions.savePayment(term, record)} />}
  </React.Fragment>;
}

Object.assign(window, { ApVatSurfaceModal, apVatCandidateTerms, apVatSurfaceYears });
